import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Loader2, Package, Truck, CheckCircle, Clock, AlertCircle, XCircle, RotateCcw, RefreshCw, Mail, Phone } from 'lucide-react';
import { useOrders } from '../hooks/useOrders';
import { useShippingMode } from '../context/ShippingModeContext';
import orderService from '../services/order.service';
import { sanitizeError } from '../utils/errorUtils';
import { generateInvoice } from '../utils/generateInvoice';
import { Printer } from 'lucide-react';

const OrderList = () => {
  const navigate = useNavigate();
  const { orders, loading, error, fetchOrders } = useOrders();
  const { shippingMode } = useShippingMode();
  const [filters, setFilters] = useState({
    status: '',
    deliveryPartner: '',
    limit: 50
  });
  const [actionMessage, setActionMessage] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [trackingOrders, setTrackingOrders] = useState(new Set());
  const [trackingDisplayStatuses, setTrackingDisplayStatuses] = useState({});
  const [invoiceBusy, setInvoiceBusy] = useState(null);

  useEffect(() => {
    fetchOrders({ ...filters, type: shippingMode });
  }, [filters, shippingMode, fetchOrders]);

  const handleTrackOrder = async (e, order) => {
    e.stopPropagation();
    if (!order.awb) return;
    setTrackingOrders(prev => new Set(prev).add(order._id));
    try {
      const result = await orderService.trackOrder(order._id);
      const newStatus = result?.data?.status || result?.status;
      const displayStatus = result?.data?.displayStatus || result?.displayStatus || newStatus;
      setTrackingDisplayStatuses(prev => ({ ...prev, [order._id]: displayStatus?.replace(/_/g, ' ').toUpperCase() || 'UPDATED' }));
      if (newStatus) {
        order.status = newStatus;
      }
      fetchOrders({ ...filters, type: shippingMode });
    } catch (err) {
      console.error('Track failed:', err.message);
    } finally {
      setTrackingOrders(prev => {
        const next = new Set(prev);
        next.delete(order._id);
        return next;
      });
    }
  };

  const handleIntlInvoice = async (e, order) => {
    e.stopPropagation();
    setActionMessage(null);
    setInvoiceBusy(order._id);
    try {
      await orderService.downloadInvoice(order._id, { inline: true });
      setActionMessage({ type: 'ok', text: 'Invoice opened in new tab ✓' });
    } catch (err) {
      setActionMessage({
        type: 'err',
        text: sanitizeError(err.message || err.response?.data?.message || 'Invoice download failed')
      });
    } finally {
      setInvoiceBusy(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'picked_up':
      case 'in_transit':
      case 'out_for_delivery':
        return <Truck className="w-4 h-4 text-blue-600" />;
      case 'pending':
      case 'confirmed':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'cancelled':
      case 'lost':
      case 'damaged':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'rto':
      case 'rto_delivered':
        return <RotateCcw className="w-4 h-4 text-orange-600" />;
      default:
        return <Package className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'picked_up':
      case 'in_transit':
      case 'out_for_delivery':
        return 'bg-blue-100 text-blue-700';
      case 'pending':
      case 'confirmed':
        return 'bg-yellow-100 text-yellow-700';
      case 'cancelled':
      case 'lost':
      case 'damaged':
        return 'bg-red-100 text-red-700';
      case 'rto':
      case 'rto_delivered':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const filteredOrders = orders?.filter((order) => {
    const search = searchValue.toLowerCase();

    return (
      order.orderNumber?.toLowerCase().includes(search) ||
      order.awb?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen font-sans text-[#1a2b4b]">
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold">Orders</h1>
        <div className="flex gap-3">
          <a href="tel:+918826885557" className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-blue-600 transition">
            <Phone className="w-4 h-4" />
            +918826885557
          </a>
          <a
            href="mailto:support@flywelllogistics.com"
            className="flex items-center px-3 py-1.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition">
            <Mail className="w-4 h-4 mr-2" />
            Email Us
          </a>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-2">
        <div className="relative flex items-center min-w-[280px] border border-gray-300 rounded-lg bg-white">
          <Search className="absolute left-3 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order number or AWB"
            className="w-full pl-10 pr-4 py-1.5 text-sm focus:outline-none"
            value={searchValue}
            onChange={(e)=> setSearchValue(e.target.value)}
          />
        </div>

        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 bg-[#f1f4f9] min-w-[140px]"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="picked_up">Picked Up</option>
          <option value="in_transit">In Transit</option>
          <option value="out_for_delivery">Out for Delivery</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
          <option value="rto">RTO</option>
          <option value="rto_delivered">RTO Delivered</option>
          <option value="lost">Lost</option>
          <option value="damaged">Damaged</option>
        </select>

        <select
          value={filters.deliveryPartner}
          onChange={(e) => setFilters({ ...filters, deliveryPartner: e.target.value })}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 bg-[#f1f4f9] min-w-[140px]"
        >
          <option value="">All Couriers</option>
          {(shippingMode === 'domestic' || !shippingMode) && (
            <>
              <option value="delhivery">Delhivery</option>
              <option value="nimbuspost">NimbusPost</option>
            </>
          )}
          {(shippingMode === 'international' || !shippingMode) && (
            <>
              <option value="skart">Skart (International)</option>
              <option value="delhivery_international">Delhivery International</option>
            </>
          )}
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}
      {actionMessage && (
        <div className={`mb-4 p-3 border rounded-lg text-sm flex items-center gap-2 ${actionMessage.type === 'err' ? 'bg-red-50 border-red-200 text-red-600' : 'bg-green-50 border-green-200 text-green-600'}`}>
          {actionMessage.type === 'err' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          <span>{actionMessage.text}</span>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600">Loading orders...</p>
          </div>
        ) : orders && orders.length > 0 ? (
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#e9ecef] text-[11px] uppercase text-gray-700 font-extrabold border-b border-gray-200">
              <tr>
                <th className="px-3 py-2">Order Number</th>
                <th className="px-3 py-2">AWB</th>
                <th className="px-3 py-2">Pickup → Delivery</th>
                <th className="px-3 py-2">Courier</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="text-[12px]">
              {filteredOrders.map((order) => (
                <tr
                  key={order._id}
                  className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/order-details?id=${order._id}`)}
                >
                  <td className="px-3 py-3">
                    <p className="text-blue-600 font-bold">{order.orderNumber}</p>
                  </td>
                  <td className="px-3 py-3 font-black text-[#1a2b4b]">
                    {order.awb || '-'}
                  </td>
                  <td className="px-3 py-3 text-gray-700 font-bold">
                    <p className="truncate max-w-[200px]">{order.pickupDetails?.pincode || '-'}</p>
                    <p className="text-gray-400">→</p>
                    <p className="truncate max-w-[200px]">{order.deliveryDetails?.pincode || '-'}</p>
                  </td>
                  <td className="px-3 py-3 text-gray-700 font-bold capitalize">
                    {order.metadata?.courierName || 
                     order.metadata?.nimbusCourierName || 
                     order.metadata?.skartCourierName || 
                     order.deliveryPartner?.replace('_', ' ')}
                  </td>
                  <td className="px-3 py-3 font-bold text-gray-700">
                    {formatAmount(order.pricing?.totalAmount)}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(order.status)}`}>
                      {getStatusIcon(order.status)}
                      {trackingDisplayStatuses[order._id] || order.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-gray-600 font-bold">
                    {formatDate(order.createdAt)}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/order-details?id=${order._id}`);
                        }}
                        className="text-blue-600 hover:text-blue-700 font-bold text-xs"
                      >
                        View
                      </button>
                      {(order.deliveryPartner === 'delhivery_international' || order.deliveryPartner === 'skart') ? (
                        <button
                          onClick={(e) => handleIntlInvoice(e, order)}
                          disabled={invoiceBusy === order._id || !order.awb}
                          className="text-emerald-600 hover:text-emerald-700 font-bold text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Download carrier invoice"
                        >
                          {invoiceBusy === order._id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Printer size={14} />
                          )}
                          Invoice
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            generateInvoice(order);
                          }}
                          className="text-emerald-600 hover:text-emerald-700 font-bold text-xs flex items-center gap-1"
                          title="Print Invoice"
                        >
                          <Printer size={14} />
                          Invoice
                        </button>
                      )}
                      {order.awb && !['delivered', 'cancelled', 'rto_delivered', 'lost', 'damaged'].includes(order.status) && (
                        <button
                          onClick={(e) => handleTrackOrder(e, order)}
                          disabled={trackingOrders.has(order._id)}
                          className="text-purple-600 hover:text-purple-700 font-bold text-xs flex items-center gap-1 disabled:opacity-50"
                          title="Refresh Tracking"
                        >
                          <RefreshCw size={14} className={trackingOrders.has(order._id) ? 'animate-spin' : ''} />
                          Track
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-semibold mb-2">No orders found</p>
            <p className="text-gray-500 text-sm">Create your first order to get started</p>
            <button
              onClick={() => navigate('/create-order')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              Create Order
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderList;
